import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type {
  AgencyUser,
  HarbFilesResponse,
  HarbScrapeStatus,
  Lead,
  LeadStage,
  Quote,
  Role,
} from '../types';
import { ChatThread } from '../components/ChatThread';
import { QuoteCards } from '../components/QuoteCards';
import {
  COVERAGE_LABELS,
  DRIVERS_LABELS,
  LEAD_DEFAULTS,
  SYSTEM_STATUS_LABELS,
  formatDateTime,
  formatPrice,
  systemStatusLabel,
} from '../leadLabels';
import { leadFieldIssues, hasBlockingError, type FieldIssue } from '../leadValidation';

type Draft = Partial<Lead>;

/** Hebrew label for the Har Habituach scrape status badge. */
function harbStatusLabel(status?: HarbScrapeStatus): string {
  switch (status) {
    case 'pending':
      return 'בהמתנה';
    case 'running':
      return 'בתהליך';
    case 'completed':
      return 'הושלם';
    case 'blocked_captcha':
      return 'נחסם (CAPTCHA) — נדרשת הרצה ידנית';
    case 'failed':
      return 'נכשל';
    default:
      return 'לא נמשך';
  }
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const QUOTE_POLL_INTERVAL_MS = 2500;
const QUOTE_POLL_TIMEOUT_MS = 5 * 60 * 1000;

function isQuoteInFlight(lead?: Lead | null): boolean {
  return lead?.quote_status === 'pending' || lead?.quote_status === 'running';
}

function quoteResultNote(lead: Lead): string {
  if (lead.quote_status === 'failed') {
    return `הרצת תמחור נכשלה: ${lead.quote_error || 'שגיאה לא ידועה'}`;
  }
  const q = lead.last_quote;
  if (q?.source === 'qualla') return 'התקבלו הצעות חיות מחברות הביטוח.';
  return `תמחור הושלם (מקור: ${q?.source ?? 'stub'}${q?.failureReason ? ` — ${q.failureReason}` : ''}).`;
}

function storedQuoteAsQuote(lead: Lead): Quote | null {
  const q = lead.last_quote;
  if (!q) return null;
  return {
    currency: q.currency,
    annualPremium: q.annualPremium,
    bids: q.bids,
    scannedPaths: q.scannedPaths,
    highestPrice: q.highestPrice,
    source: q.source,
    failureReason: q.failureReason,
  };
}

/** Inline validation message shown beneath a field (mirrors the bot's rules). */
function IssueNote({ issue }: { issue?: FieldIssue }) {
  if (!issue) return null;
  return <span className={`field-issue ${issue.level}`}>{issue.text}</span>;
}

/**
 * A single editable field bound to the draft-over-lead value. When `defaultLabel`
 * is provided (field is empty), a "ברירת מחדל" badge shows the value the pricing
 * engine will use if the agent leaves it blank.
 */
function Field({
  label,
  defaultLabel,
  children,
}: {
  label: string;
  defaultLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="lead-field">
      <span className="lead-field-label">
        {label}
        {defaultLabel && <span className="lead-default-badge">ברירת מחדל: {defaultLabel}</span>}
      </span>
      {children}
    </label>
  );
}

/**
 * Full lead record: collapsible sections for every bot field (editable), the
 * quote panel (run pricing / send to customer), the not-interested control, and
 * the embedded WhatsApp conversation.
 */
export function LeadDetail({
  phone,
  tenantId,
  role,
  onBack,
}: {
  phone: string;
  tenantId?: string;
  role: Role;
  onBack: () => void;
}) {
  const [lead, setLead] = useState<Lead | null>(null);
  const [stage, setStage] = useState<LeadStage | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [looking, setLooking] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [sending, setSending] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [freshQuote, setFreshQuote] = useState<Quote | null>(null);
  const [harb, setHarb] = useState<HarbFilesResponse | null>(null);
  const [harbDownloading, setHarbDownloading] = useState<number | null>(null);
  // Agency users for the assignee picker (fails silently for non-managers).
  const [users, setUsers] = useState<AgencyUser[]>([]);
  const quotePollGen = useRef(0);
  const quotePollActive = useRef(false);

  const canEdit = role === 'Agent' || role === 'AgencyAdmin' || role === 'SystemAdmin';

  useEffect(() => {
    api
      .listUsers(tenantId)
      .then(setUsers)
      .catch(() => undefined);
  }, [tenantId]);

  function load() {
    api
      .getLead(phone, tenantId)
      .then((r) => {
        setLead(r.lead);
        setStage(r.stage);
        setDraft({});
      })
      .catch((e) => setNote(String(e)));
  }

  function loadHarb() {
    api
      .getHarbFiles(phone, tenantId)
      .then((r) => setHarb(r))
      .catch(() => setHarb(null));
  }

  useEffect(() => {
    load();
    loadHarb();
    return () => {
      quotePollGen.current += 1;
      quotePollActive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, tenantId]);

  useEffect(() => {
    if (!isQuoteInFlight(lead) || quotePollActive.current) return;
    void pollUntilQuoteSettled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, tenantId, lead?.quote_status]);

  async function downloadHarb(index: number, name: string) {
    setHarbDownloading(index);
    setNote(null);
    try {
      await api.downloadHarbFile(phone, index, name, tenantId);
    } catch (e) {
      setNote(`הורדת הקובץ נכשלה: ${e}`);
    } finally {
      setHarbDownloading(null);
    }
  }

  // Read a field's current value (draft override, else the persisted lead).
  function val<K extends keyof Lead>(key: K): Lead[K] | undefined {
    return (key in draft ? draft[key] : lead?.[key]) as Lead[K] | undefined;
  }
  function set<K extends keyof Lead>(key: K, value: Lead[K] | undefined) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  // Helpers for typed inputs.
  const textVal = (k: keyof Lead) => (val(k) ?? '') as string;
  const numVal = (k: keyof Lead) => {
    const v = val(k);
    return v === undefined || v === null ? '' : String(v);
  };
  const boolSel = (k: keyof Lead) => {
    const v = val(k);
    return v === undefined ? '' : v ? 'true' : 'false';
  };
  const setBool = (k: keyof Lead, s: string) =>
    set(k, (s === '' ? undefined : s === 'true') as never);
  const setNum = (k: keyof Lead, s: string) =>
    set(k, (s === '' ? undefined : Number(s)) as never);
  // The default-value badge text for a field, shown only while it's empty.
  const defaultHint = (k: keyof Lead): string | undefined => {
    const v = val(k);
    const empty = v === undefined || v === null || v === '';
    return empty ? LEAD_DEFAULTS[k as string] : undefined;
  };

  // Keep a live ref to the draft so the debounced auto-save always flushes the
  // latest values without stale closures.
  const draftRef = useRef<Draft>({});
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  /** Persist all pending edits. Clears only the keys that were actually saved. */
  async function flush(): Promise<void> {
    const snapshot = draftRef.current;
    const keys = Object.keys(snapshot);
    if (keys.length === 0) return;
    setSaveState('saving');
    setNote(null);
    try {
      const r = await api.updateLead(phone, snapshot, tenantId);
      setLead(r.lead);
      setStage(r.stage);
      setDraft((d) => {
        const next: Draft = { ...d };
        for (const k of keys) {
          if ((next as Record<string, unknown>)[k] === (snapshot as Record<string, unknown>)[k]) {
            delete (next as Record<string, unknown>)[k];
          }
        }
        return next;
      });
      setSaveState('saved');
    } catch (e) {
      setSaveState('error');
      setNote(`שמירה נכשלה: ${e}`);
    }
  }

  /** Flush immediately (used before running a quote so it prices the latest data). */
  async function flushNow(): Promise<void> {
    if (Object.keys(draftRef.current).length) await flush();
  }

  // Auto-save: debounce edits and persist them ~0.8s after the last keystroke,
  // so the agent never needs a "save" button — changes stick as they type.
  useEffect(() => {
    if (!canEdit || Object.keys(draft).length === 0) return;
    const t = setTimeout(() => {
      void flush();
    }, 800);
    return () => clearTimeout(t);
  }, [draft, canEdit]);

  async function lookupCar() {
    const carNumber = ((val('car_number') ?? '') as string).trim();
    if (!carNumber) {
      setNote('הזן מספר רכב לשליפה.');
      return;
    }
    setLooking(true);
    setNote(null);
    try {
      const v = await api.lookupVehicle(carNumber, tenantId);
      if (v.lookupFailed) {
        setNote('שליפת פרטי הרכב נכשלה (משרד התחבורה אינו זמין כרגע).');
      } else if (!v.found) {
        setNote('לא נמצא רכב עם המספר שהוזן במרשם משרד התחבורה.');
      } else {
        setDraft((d) => ({
          ...d,
          car_manufacturer: v.manufacturer ?? d.car_manufacturer ?? lead?.car_manufacturer,
          car_model: v.model ?? d.car_model ?? lead?.car_model,
          car_year: v.year ?? d.car_year ?? lead?.car_year,
          car_color: v.color ?? d.car_color ?? lead?.car_color,
          car_fuel: v.fuel ?? d.car_fuel ?? lead?.car_fuel,
        }));
        setNote(`נמצא רכב: ${[v.manufacturer, v.model, v.year].filter(Boolean).join(' ')} — הפרטים מולאו ונשמרים אוטומטית.`);
      }
    } catch (e) {
      setNote(`שליפת פרטי הרכב נכשלה: ${e}`);
    } finally {
      setLooking(false);
    }
  }

  async function pollUntilQuoteSettled() {
    if (quotePollActive.current) return;
    quotePollActive.current = true;
    const gen = ++quotePollGen.current;
    setQuoting(true);
    setNote('משווה מחירים מול חברות הביטוח, זה יכול לקחת עד כשתי דקות…');
    const deadline = Date.now() + QUOTE_POLL_TIMEOUT_MS;
    try {
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, QUOTE_POLL_INTERVAL_MS));
        if (gen !== quotePollGen.current) return;
        const r = await api.getLead(phone, tenantId);
        if (gen !== quotePollGen.current) return;
        setLead(r.lead);
        setStage(r.stage);
        if (r.lead.quote_status === 'done') {
          setFreshQuote(storedQuoteAsQuote(r.lead));
          setNote(quoteResultNote(r.lead));
          return;
        }
        if (r.lead.quote_status === 'failed') {
          setNote(quoteResultNote(r.lead));
          return;
        }
      }
      setNote('התמחור עדיין רץ ברקע. ניתן לרענן את הדף בעוד כדקה, או לנסות שוב.');
    } catch (e) {
      if (gen !== quotePollGen.current) return;
      setNote(`הרצת תמחור נכשלה: ${e}`);
    } finally {
      if (gen === quotePollGen.current) {
        quotePollActive.current = false;
        setQuoting(false);
      }
    }
  }

  async function runQuote() {
    setQuoting(true);
    setNote('משווה מחירים מול חברות הביטוח, זה יכול לקחת עד כשתי דקות…');
    setFreshQuote(null);
    try {
      // Persist any pending edits first so the quote prices the latest values.
      await flushNow();
      const r = await api.runQuote(phone, tenantId);
      setLead(r.lead);
      await pollUntilQuoteSettled();
    } catch (e) {
      setNote(`הרצת תמחור נכשלה: ${e}`);
      setQuoting(false);
    }
  }

  async function sendQuote() {
    if (!window.confirm('לשלוח את ההצעה האחרונה ללקוח בוואטסאפ?')) return;
    setSending(true);
    setNote(null);
    try {
      await api.sendQuote(phone, tenantId);
      setNote('ההצעה נשלחה ללקוח.');
    } catch (e) {
      setNote(`שליחת ההצעה נכשלה: ${e}`);
    } finally {
      setSending(false);
    }
  }

  async function toggleArchive() {
    if (!lead) return;
    const archive = !lead.archived;
    if (archive && !window.confirm('להעביר את הליד לארכיון? הנתונים יישמרו וניתן לשחזר.')) return;
    setArchiving(true);
    try {
      const r = archive
        ? await api.archiveLead(phone, tenantId)
        : await api.restoreLead(phone, tenantId);
      setLead(r.lead);
      setNote(archive ? 'הליד הועבר לארכיון.' : 'הליד שוחזר.');
    } catch (e) {
      setNote(String(e));
    } finally {
      setArchiving(false);
    }
  }

  async function resetConversation() {
    if (!lead) return;
    if (
      !window.confirm(
        'לאפס את השיחה? כל הנתונים שנאספו (רכב, נהג, כיסוי, תביעות, הצעות) יימחקו והבוט יתחיל את התהליך מחדש בהודעה הבאה. הליד עצמו יישמר.',
      )
    )
      return;
    setResetting(true);
    setNote(null);
    try {
      const r = await api.resetConversation(phone, tenantId);
      setLead(r.lead);
      setFreshQuote(null);
      setNote('השיחה אופסה. ההודעה הבאה מהלקוח תתחיל תהליך חדש.');
    } catch (e) {
      setNote(`איפוס השיחה נכשל: ${e}`);
    } finally {
      setResetting(false);
    }
  }

  async function toggleNotInterested() {
    if (!lead) return;
    const next = !lead.not_interested;
    if (next && !window.confirm('לסמן את הליד כ"לא מעוניין"?')) return;
    try {
      const r = await api.setNotInterested(phone, next, tenantId);
      setLead(r.lead);
      setStage(r.stage);
    } catch (e) {
      setNote(String(e));
    }
  }

  const bids = useMemo(() => freshQuote?.bids ?? lead?.last_quote?.bids ?? [], [freshQuote, lead]);

  // Field-level validation issues (mirrors the bot's rules), recomputed as the
  // agent edits. `blocking` is true when a value the bot would reject is present.
  const issues = useMemo(
    () =>
      leadFieldIssues({
        youngest_driver_age: val('youngest_driver_age') as number | undefined,
        youngest_driver_birth_date: val('youngest_driver_birth_date') as string | undefined,
        youngest_driver_license_years: val('youngest_driver_license_years') as number | undefined,
        insurance_start_date: val('insurance_start_date') as string | undefined,
        car_number: val('car_number') as string | undefined,
        national_id: val('national_id') as string | undefined,
      }),
    [draft, lead],
  );
  const blocking = hasBlockingError(issues);

  if (!lead) {
    return (
      <div className="lead-detail">
        <div className="lead-detail-header">
          <button className="btn secondary" onClick={onBack}>
            → חזרה לרשימה
          </button>
        </div>
        <div className="empty">{note || 'טוען…'}</div>
      </div>
    );
  }

  const car = [lead.car_manufacturer, lead.car_model, lead.car_year].filter(Boolean).join(' ');

  return (
    <div className="lead-detail">
      <div className="lead-detail-header">
        <button className="btn secondary" onClick={onBack}>
          → חזרה לרשימה
        </button>
        <div className="lead-title">
          <strong>{lead.customer_name || lead.phone}</strong>
          <span
            className={`stage-badge stage-badge-primary heat-${stage?.heat || 'manual'}`}
            title="סטטוס מערכת"
          >
            {stage?.label || systemStatusLabel(lead.status)}
          </span>
          {lead.source && lead.source !== 'bot' && (
            <span className="stage-badge heat-cold">
              {lead.source === 'manual' ? 'ליד ידני' : 'יובא'}
            </span>
          )}
          {lead.archived && <span className="stage-badge heat-manual">ארכיון</span>}
        </div>
        <div className="spacer" />
        {canEdit && (
          <button className="btn outline" onClick={toggleArchive} disabled={archiving}>
            {lead.archived ? 'שחזר מהארכיון' : 'העבר לארכיון'}
          </button>
        )}
        {canEdit && (
          <button className="btn secondary" onClick={toggleNotInterested}>
            {lead.not_interested ? 'בטל סימון לא מעוניין' : 'סמן כלא מעוניין'}
          </button>
        )}
        {canEdit && (
          <button className="btn outline" onClick={resetConversation} disabled={resetting}>
            {resetting ? 'מאפס…' : 'אפס שיחה'}
          </button>
        )}
        {canEdit && (
          <span className={`autosave-status ${saveState}`}>
            {saveState === 'saving' && (
              <>
                <svg className="spin" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.2-8.5" /></svg>
                שומר…
              </>
            )}
            {saveState === 'saved' && (
              <>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                נשמר אוטומטית
              </>
            )}
            {saveState === 'error' && 'שמירה נכשלה'}
            {saveState === 'idle' && 'נשמר אוטומטית עם כל שינוי'}
          </span>
        )}
      </div>

      {note && <div className="lead-note">{note}</div>}
      {lead.manual_review_reason && (
        <div className="lead-note warn">⚠ {lead.manual_review_reason}</div>
      )}

      <div className="lead-body">
        <div className="lead-sections">
          {/* פרטי הליד */}
          <details className="lead-section" open>
            <summary>פרטי הליד</summary>
            <div className="lead-grid">
              <Field label="שם הלקוח">
                <input value={textVal('customer_name')} onChange={(e) => set('customer_name', e.target.value)} disabled={!canEdit} />
              </Field>
              <Field label="טלפון">
                <input value={lead.phone} disabled />
              </Field>
              <Field label="נציג מטפל">
                <input
                  list="agency-users-detail"
                  value={textVal('assigned_to')}
                  onChange={(e) => set('assigned_to', e.target.value)}
                  disabled={!canEdit}
                />
                <datalist id="agency-users-detail">
                  {users.map((u) => (
                    <option key={u.sub} value={u.email}>
                      {u.name ? `${u.name} (${u.email})` : u.email}
                    </option>
                  ))}
                </datalist>
              </Field>
              <Field label="ת.ז.">
                <input value={textVal('national_id')} onChange={(e) => set('national_id', e.target.value)} disabled={!canEdit} />
                <IssueNote issue={issues.national_id} />
              </Field>
              <Field label="סטטוס מערכת">
                <select
                  className="system-status-select"
                  value={textVal('status')}
                  onChange={(e) => set('status', e.target.value)}
                  disabled={!canEdit}
                >
                  {/* Keep the current value even if it's an unexpected key. */}
                  {!SYSTEM_STATUS_LABELS[textVal('status')] && textVal('status') && (
                    <option value={textVal('status')}>{systemStatusLabel(textVal('status'))}</option>
                  )}
                  {Object.entries(SYSTEM_STATUS_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="תאריך הקמה">
                <input value={formatDateTime(lead.created_at)} disabled />
              </Field>
            </div>
          </details>

          {/* פרטי הרכב */}
          <details className="lead-section" open>
            <summary>פרטי הרכב {car ? `· ${car}` : ''}</summary>
            <div className="lead-grid">
              <Field label="מספר רכב">
                <div className="input-with-btn">
                  <input value={textVal('car_number')} onChange={(e) => set('car_number', e.target.value)} disabled={!canEdit} />
                  {canEdit && (
                    <button type="button" className="btn outline sm" onClick={lookupCar} disabled={looking}>
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
                      {looking ? 'שולף…' : 'משרד התחבורה'}
                    </button>
                  )}
                </div>
                <IssueNote issue={issues.car_number} />
              </Field>
              <Field label="יצרן">
                <input value={textVal('car_manufacturer')} onChange={(e) => set('car_manufacturer', e.target.value)} disabled={!canEdit} />
              </Field>
              <Field label="דגם">
                <input value={textVal('car_model')} onChange={(e) => set('car_model', e.target.value)} disabled={!canEdit} />
              </Field>
              <Field label="שנת ייצור">
                <input type="number" value={numVal('car_year')} onChange={(e) => setNum('car_year', e.target.value)} disabled={!canEdit} />
              </Field>
              <Field label="צבע">
                <input value={textVal('car_color')} onChange={(e) => set('car_color', e.target.value)} disabled={!canEdit} />
              </Field>
              <Field label="דלק">
                <input value={textVal('car_fuel')} onChange={(e) => set('car_fuel', e.target.value)} disabled={!canEdit} />
              </Field>
            </div>
          </details>

          {/* פרטי הנהג */}
          <details className="lead-section" open>
            <summary>פרטי הנהג והכיסוי</summary>
            <div className="lead-grid">
              <Field label="גיל נהג צעיר" defaultLabel={defaultHint('youngest_driver_age')}>
                <input type="number" value={numVal('youngest_driver_age')} onChange={(e) => setNum('youngest_driver_age', e.target.value)} disabled={!canEdit} />
                <IssueNote issue={issues.youngest_driver_age} />
              </Field>
              <Field label="תאריך לידה מדויק" defaultLabel={defaultHint('youngest_driver_birth_date')}>
                <input type="date" value={textVal('youngest_driver_birth_date')} onChange={(e) => set('youngest_driver_birth_date', e.target.value || undefined)} disabled={!canEdit} />
                <IssueNote issue={issues.youngest_driver_birth_date} />
              </Field>
              <Field label="מגדר נהג צעיר" defaultLabel={defaultHint('youngest_driver_gender')}>
                <select value={(val('youngest_driver_gender') as string) ?? ''} onChange={(e) => set('youngest_driver_gender', (e.target.value || undefined) as never)} disabled={!canEdit}>
                  <option value="">—</option>
                  <option value="male">זכר</option>
                  <option value="female">נקבה</option>
                </select>
              </Field>
              <Field label="ותק רישיון (שנים)" defaultLabel={defaultHint('youngest_driver_license_years')}>
                <input type="number" value={numVal('youngest_driver_license_years')} onChange={(e) => setNum('youngest_driver_license_years', e.target.value)} disabled={!canEdit} />
                <IssueNote issue={issues.youngest_driver_license_years} />
              </Field>
              <Field label="מספר נהגים" defaultLabel={defaultHint('drivers_count')}>
                <select value={(val('drivers_count') as string) ?? ''} onChange={(e) => set('drivers_count', (e.target.value || undefined) as never)} disabled={!canEdit}>
                  <option value="">—</option>
                  {Object.entries(DRIVERS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
              <Field label="סוג כיסוי" defaultLabel={defaultHint('coverage_type')}>
                <select value={(val('coverage_type') as string) ?? ''} onChange={(e) => set('coverage_type', (e.target.value || undefined) as never)} disabled={!canEdit}>
                  <option value="">—</option>
                  {Object.entries(COVERAGE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
              <Field label="תאריך תחילת ביטוח" defaultLabel={defaultHint('insurance_start_date')}>
                <input type="date" value={textVal('insurance_start_date')} onChange={(e) => set('insurance_start_date', e.target.value)} disabled={!canEdit} />
                <IssueNote issue={issues.insurance_start_date} />
              </Field>
              <Field label="עיר">
                <input value={textVal('main_driver_city')} onChange={(e) => set('main_driver_city', e.target.value)} disabled={!canEdit} />
              </Field>
              <Field label="מיקוד" defaultLabel={defaultHint('main_driver_zip')}>
                <input value={textVal('main_driver_zip')} onChange={(e) => set('main_driver_zip', e.target.value)} disabled={!canEdit} />
              </Field>
              <Field label="כתובת מלאה">
                <input value={textVal('main_driver_address')} onChange={(e) => set('main_driver_address', e.target.value)} disabled={!canEdit} />
              </Field>
              <Field label="נשוי/אה" defaultLabel={defaultHint('is_married')}>
                <select value={boolSel('is_married')} onChange={(e) => setBool('is_married', e.target.value)} disabled={!canEdit}>
                  <option value="">—</option>
                  <option value="true">כן</option>
                  <option value="false">לא</option>
                </select>
              </Field>
              <Field label="נהיגה בשבת" defaultLabel={defaultHint('drives_on_saturday')}>
                <select value={boolSel('drives_on_saturday')} onChange={(e) => setBool('drives_on_saturday', e.target.value)} disabled={!canEdit}>
                  <option value="">—</option>
                  <option value="true">כן</option>
                  <option value="false">לא</option>
                </select>
              </Field>
            </div>
          </details>

          {/* היסטוריית ביטוח */}
          <details className="lead-section">
            <summary>היסטוריית ביטוח</summary>
            <div className="lead-grid">
              {([1, 2, 3] as const).map((y) => {
                const k = `prior_coverage_${y}` as keyof Lead;
                return (
                  <Field key={y} label={`רצף - לפני ${y} שנים`} defaultLabel={defaultHint(k)}>
                    <select value={numVal(k)} onChange={(e) => setNum(k, e.target.value)} disabled={!canEdit}>
                      <option value="">—</option>
                      <option value="1">מקיף</option>
                      <option value="2">צד ג'</option>
                      <option value="0">לא היה מבוטח</option>
                    </select>
                  </Field>
                );
              })}
              <Field label="מספר תביעות (3 שנים)">
                <input type="number" value={numVal('prior_claims_count')} onChange={(e) => setNum('prior_claims_count', e.target.value)} disabled={!canEdit} />
              </Field>
              <Field label="רכב משועבד">
                <select value={boolSel('is_pledged')} onChange={(e) => setBool('is_pledged', e.target.value)} disabled={!canEdit}>
                  <option value="">—</option>
                  <option value="true">כן</option>
                  <option value="false">לא</option>
                </select>
              </Field>
              <Field label="רכב חברה נוסף במשק הבית">
                <select value={boolSel('spouse_company_car')} onChange={(e) => setBool('spouse_company_car', e.target.value)} disabled={!canEdit}>
                  <option value="">—</option>
                  <option value="true">כן</option>
                  <option value="false">לא</option>
                </select>
              </Field>
            </div>
          </details>

          {/* הצעות מחיר */}
          <details className="lead-section" open>
            <summary>הצעות מחיר</summary>
            {blocking && (
              <div className="lead-note warn">
                ⚠ יש שדות עם ערכים שאינם עומדים בכללי החיתום (מסומנים באדום למעלה). ניתן עדיין להריץ השוואת מחירים, אך ייתכן שהתוצאה לא תהיה תקפה.
              </div>
            )}
            <div className="quote-actions">
              {canEdit && (
                <button className="btn" onClick={runQuote} disabled={quoting || isQuoteInFlight(lead)}>
                  {quoting || isQuoteInFlight(lead) ? 'משווה מחירים…' : 'השוואת מחירים מול חברות הביטוח'}
                </button>
              )}
              {canEdit && (
                <button
                  className="btn secondary"
                  onClick={sendQuote}
                  disabled={sending || quoting || isQuoteInFlight(lead) || !lead.last_quote || lead.opt_out}
                  title={lead.opt_out ? 'הלקוח הוסר מרשימת התפוצה — שליחה חסומה' : quoting ? 'ממתינים לסיום השוואת המחירים' : undefined}
                >
                  {sending ? 'שולח…' : 'שלח הצעה ללקוח'}
                </button>
              )}
              {lead.last_quote && (
                <span className="cell-sub">
                  עודכן {formatDateTime(lead.last_quote.ran_at)} · {lead.last_quote.ran_by}
                  {lead.last_quote.scannedPaths
                    ? ` · נסרקו ${lead.last_quote.scannedPaths.toLocaleString('he-IL')} מסלולים · ${bids.length.toLocaleString('he-IL')} הצעות`
                    : bids.length
                      ? ` · ${bids.length.toLocaleString('he-IL')} הצעות`
                      : ''}
                </span>
              )}
            </div>
            {bids.length > 0 ? (
              <QuoteCards bids={bids} currency={lead.last_quote?.currency} />
            ) : (
              <div className="empty">
                {lead.last_quote
                  ? `הצעה: ${formatPrice(lead.last_quote.annualPremium, lead.last_quote.currency)} (מקור: ${lead.last_quote.source})`
                  : 'טרם הורצה תמחור.'}
              </div>
            )}
          </details>

          {/* מסמכי הר הביטוח */}
          <details className="lead-section">
            <summary>מסמכי הר הביטוח</summary>
            <div className="harb-status">
              <span className="cell-sub">
                סטטוס: {harbStatusLabel(lead.harb_scrape_status)}
                {lead.harb_scraped_at ? ` · עודכן ${formatDateTime(lead.harb_scraped_at)}` : ''}
                {lead.harb_consent_at ? ` · הסכמה ${formatDateTime(lead.harb_consent_at)}` : ''}
              </span>
              {(lead.harb_scrape_status === 'blocked_captcha' ||
                lead.harb_scrape_status === 'failed') &&
                harb?.manual_review_reason && (
                  <div className="empty">{harb.manual_review_reason}</div>
                )}
            </div>
            {harb && harb.files.length > 0 ? (
              <ul className="harb-files">
                {harb.files.map((f) => (
                  <li key={f.index} className="harb-file">
                    <span className="harb-file-name">{f.name}</span>
                    <span className="cell-sub">
                      {f.uploadedAt ? formatDateTime(f.uploadedAt) : ''}
                      {typeof f.size === 'number' ? ` · ${Math.round(f.size / 1024)} KB` : ''}
                    </span>
                    <button
                      className="btn secondary"
                      onClick={() => downloadHarb(f.index, f.name)}
                      disabled={harbDownloading !== null}
                    >
                      {harbDownloading === f.index ? 'מוריד…' : 'הורדה'}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="empty">
                {lead.harb_scrape_status === 'completed'
                  ? 'לא נמצאו קבצים.'
                  : 'טרם נמשכו נתוני הר הביטוח.'}
              </div>
            )}
          </details>
        </div>

        {/* שיחה */}
        <div className="lead-chat">
          <ChatThread phone={phone} tenantId={tenantId} role={role} />
        </div>
      </div>
    </div>
  );
}
